/**
 * Admin → Instruments: Add instrument (search the providers, then a form they filled in) and Archive.
 *
 * Step 1 searches every provider with a key for a name or ticker and shows which can price each match
 * live (green), whether it is already listed, and its price. Step 2 is the instrument with everything
 * the providers could tell pre-filled and marked AUTO: name, decimals, lot steps, trading hours, the
 * symbol at each provider, a live price, and pricing defaults of its category. Only pricing is a choice.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  fmt,
  HOURS_PRESETS,
  PROVIDER_BY_ID,
  ROUTE_CLASS_LABEL,
  type AssetSearch,
  type AssetSearchResult,
  type InstrumentCategory,
  type InstrumentDraft,
  type PriceProviderId,
  type RouteClass,
} from '@platform/shared';
import { cx, Modal, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const secondary = 'h-9 rounded-lg border border-line px-4 text-xs font-semibold text-fg disabled:opacity-50';
const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
const CLASSES: (RouteClass | null)[] = [null, 'STOCK', 'FX', 'CRYPTO', 'INDEX', 'COMMODITY'];

function Logo({ symbol, url, dim }: { symbol: string; url?: string | null; dim?: boolean }) {
  if (url) return <img src={url} alt="" className={cx('size-8 shrink-0 rounded-lg bg-raised object-contain', dim && 'opacity-50')} />;
  return <span className={cx('grid size-8 shrink-0 place-items-center rounded-lg bg-raised text-[10.5px] font-extrabold', dim && 'opacity-50')}>{symbol.slice(0, 2)}</span>;
}

function SearchStep({ onPick, onClose }: { onPick: (r: AssetSearchResult) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cls, setCls] = useState<RouteClass | null>(null);
  const [results, setResults] = useState<AssetSearchResult[] | null>(null);
  const [unavailable, setUnavailable] = useState<PriceProviderId[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(null);
      return;
    }
    const my = ++seq.current;
    const t = window.setTimeout(async () => {
      setBusy(true);
      setErr(null);
      try {
        const { results: r, unavailable: down } = await api.get<AssetSearch>(`/api/admin/instruments/search?q=${encodeURIComponent(term)}${cls ? `&class=${cls}` : ''}`);
        if (my !== seq.current) return;
        setResults(r);
        setUnavailable(down);
        setSel(r.find((x) => !x.listed && x.providers.length)?.key ?? null);
      } catch (e) {
        if (my === seq.current) setErr(errText(e));
      } finally {
        if (my === seq.current) setBusy(false);
      }
    }, 350);
    return () => window.clearTimeout(t);
  }, [q, cls]);
  const picked = results?.find((r) => r.key === sel) ?? null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded-xl border border-accent bg-bg px-3 shadow-[0_0_0_3px_var(--accent-bg)]">
        <span className="text-muted" aria-hidden>
          ⌕
        </span>
        <input autoFocus className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none" placeholder="Name or ticker: nvidia, gold, eurusd, pepe…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search providers" />
        {busy && <Spinner />}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {CLASSES.map((c) => (
          <button
            key={c ?? 'all'}
            type="button"
            aria-pressed={cls === c}
            onClick={() => setCls(c)}
            className={cx('h-7 rounded-full border px-3 text-xs font-semibold', cls === c ? 'border-accent bg-accent text-white' : 'border-line text-muted')}
          >
            {c ? ROUTE_CLASS_LABEL[c] : 'All'}
          </button>
        ))}
      </div>
      {err && <p className="m-0 text-xs text-down">{err}</p>}
      {unavailable.length > 0 && (
        <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">
          {unavailable.map((p) => PROVIDER_BY_ID[p].label).join(', ')} didn't answer, so {unavailable.length === 1 ? 'its' : 'their'} assets are missing. Try again in a moment, or check Admin → Data providers.
        </p>
      )}
      {results && (
        <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-line">
          {results.length === 0 && <p className="m-0 px-4 py-6 text-center text-xs text-muted">{unavailable.length ? 'Nothing found from the providers that answered.' : `No provider with a key lists “${q}”.`}</p>}
          {results.map((r) => {
            const off = !!r.listed || !r.providers.length;
            return (
              <button
                key={r.key}
                type="button"
                disabled={off}
                onClick={() => setSel(r.key)}
                onDoubleClick={() => !off && onPick(r)}
                className={cx('flex w-full items-center gap-3 border-b border-line px-3 py-2.5 text-left last:border-0', sel === r.key && 'bg-accent-bg', off && 'cursor-default')}
              >
                <Logo symbol={r.symbol} url={r.logo} dim={off} />
                <span className={cx('min-w-0 flex-1', off && 'opacity-60')}>
                  <b className="text-[13px]">{r.symbol}</b> <span className="text-muted">{r.name}</span>
                  <span className="block text-[11px] text-muted">
                    {ROUTE_CLASS_LABEL[r.assetClass]}
                    {r.exchange ? ` · ${r.exchange}` : ''}
                    {r.currency && r.currency !== 'USD' ? ` (${r.currency})` : ''}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="num text-xs">{r.price ? fmt(r.price, Math.min(8, Math.max(2, 6 - Math.floor(Math.log10(Number(r.price) || 1))))) : ''}</span>
                  <span className="flex flex-wrap justify-end gap-1">
                    {r.listed ? (
                      <span className="rounded bg-raised px-1.5 py-px text-[10.5px] font-bold text-muted">Already listed{r.listed !== r.symbol ? ` as ${r.listed}` : ''}</span>
                    ) : r.providers.length ? (
                      r.providers.map((p) => (
                        <span key={p} className="rounded bg-up-bg px-1.5 py-px text-[10.5px] font-bold text-up">
                          {PROVIDER_BY_ID[p].label}
                        </span>
                      ))
                    ) : (
                      <span className="rounded bg-raised px-1.5 py-px text-[10.5px] font-bold text-muted">No provider with a key</span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-[11.5px] text-muted">Green = providers that can price it live</span>
        <span className="flex-1" />
        <button type="button" className={secondary} onClick={onClose}>
          Cancel
        </button>
        <button type="button" className={primary} disabled={!picked} onClick={() => picked && onPick(picked)}>
          Next: review details →
        </button>
      </div>
    </div>
  );
}

const AUTO = <span className="ml-1.5 text-[10px] font-bold text-accent-fg">AUTO</span>;

function Field({ label, auto, children, grow = 1 }: { label: string; auto?: boolean; children: React.ReactNode; grow?: number }) {
  return (
    <label className="flex min-w-[120px] flex-col gap-1.5 text-[11.5px] font-semibold text-muted" style={{ flex: grow }}>
      <span>
        {label}
        {auto && AUTO}
      </span>
      {children}
    </label>
  );
}

function DetailsStep({ hit, categories, onBack, onCreated }: { hit: AssetSearchResult; categories: InstrumentCategory[]; onBack: () => void; onCreated: (symbol: string) => void }) {
  const [d, setD] = useState<InstrumentDraft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [enable, setEnable] = useState(false);
  useEffect(() => {
    api
      .get<InstrumentDraft>(`/api/admin/instruments/lookup?key=${encodeURIComponent(hit.key)}`)
      .then(setD)
      .catch((e) => setErr(errText(e)));
  }, [hit.key]);
  if (!d) return err ? <p className="m-0 text-xs text-down">{err}</p> : <Spinner />;
  const a = (k: string) => d.auto.includes(k);
  const set = <K extends keyof InstrumentDraft>(k: K, v: InstrumentDraft[K]) => setD({ ...d, [k]: v });
  const setSym = (p: PriceProviderId, v: string) => setD({ ...d, providerSymbols: { ...d.providerSymbols, [p]: v } });
  const cat = categories.find((c) => c.id === d.categoryId);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { info: _i, live: _l, providers: _p, auto: _a, listed: _x, hoursPreset: _h, maxLeverage: _m, ...body } = d;
      await api.post('/api/admin/instruments', { ...body, symbol: d.symbol.trim().toUpperCase(), enabled: enable });
      onCreated(d.symbol);
    } catch (e2) {
      setErr(errText(e2));
    } finally {
      setBusy(false);
    }
  };
  const symbolProviders = Object.keys(d.providerSymbols) as PriceProviderId[];
  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <div className="flex items-center gap-3 rounded-xl border border-line px-4 py-3">
        <Logo symbol={d.symbol} url={d.info.logo} />
        <span className="min-w-0 flex-1">
          <b className="text-[15px]">{d.symbol}</b> · {d.name}
          <span className="block text-[11.5px] text-muted">
            {[d.info.exchange, d.info.industry, d.info.marketCap ? `Market cap $${fmt(Number(d.info.marketCap) / 1e9, 1)}B` : null, d.info.currency].filter(Boolean).join(' · ')}
          </span>
        </span>
        {d.live ? (
          <span className="text-right">
            <span className="num block text-[15px]">
              {d.live.bid} / {d.live.ask}
            </span>
            <span className="text-[11.5px] font-semibold text-up">● Live from {PROVIDER_BY_ID[d.live.provider].label}</span>
          </span>
        ) : (
          <span className="text-[11.5px] text-muted">No live price right now (market closed?)</span>
        )}
      </div>
      {d.listed && <p className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn">Already listed as {d.listed}.</p>}
      <div className="flex flex-wrap gap-3">
        <Field label="Symbol" auto={a('symbol')}>
          <input className={cx(input, 'uppercase')} value={d.symbol} onChange={(e) => set('symbol', e.target.value.toUpperCase())} />
        </Field>
        <Field label="Name" auto={a('name')} grow={2}>
          <input className={input} value={d.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Asset class" auto>
          <input className={cx(input, 'text-muted')} value={ROUTE_CLASS_LABEL[d.assetClass]} readOnly />
        </Field>
      </div>
      <div className="flex flex-wrap gap-3">
        <Field label="Category" auto={a('categoryId')}>
          <select className={input} value={d.categoryId ?? ''} onChange={(e) => set('categoryId', Number(e.target.value))}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Price decimals" auto={a('priceDecimals')}>
          <input className={cx(input, 'num')} inputMode="numeric" value={d.priceDecimals} onChange={(e) => set('priceDecimals', Number(e.target.value) || 0)} />
        </Field>
        <Field label="Lot size / step" auto>
          <input className={cx(input, 'num text-muted')} value={`${d.lotSize} / ${d.lotStep}`} readOnly />
        </Field>
        <Field label="Trading hours" auto={a('tradingHours')} grow={1.6}>
          <select
            className={input}
            value={d.hoursPreset}
            onChange={(e) => {
              const p = HOURS_PRESETS.find((x) => x.id === e.target.value);
              if (p) setD({ ...d, hoursPreset: p.id, tradingHours: p.hours });
            }}
          >
            {d.hoursPreset === 'custom' && <option value="custom">Custom</option>}
            {HOURS_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-3">
        {symbolProviders.map((p) => (
          <Field key={p} label={`${PROVIDER_BY_ID[p].label} symbol`} auto={a('providerSymbols')}>
            <input className={cx(input, 'num')} value={d.providerSymbols[p] ?? ''} onChange={(e) => setSym(p, e.target.value)} />
          </Field>
        ))}
      </div>
      <div className="border-t border-line" />
      <span className="text-[11.5px] font-semibold text-muted">PRICING · suggested from the category defaults, set by you</span>
      <div className="flex flex-wrap gap-3">
        <Field label="Spread (bps)">
          <input className={cx(input, 'num')} inputMode="decimal" value={d.spreadBps} onChange={(e) => set('spreadBps', e.target.value)} />
        </Field>
        <Field label="Markup (bps)">
          <input className={cx(input, 'num')} inputMode="decimal" value={d.markupBps} onChange={(e) => set('markupBps', e.target.value)} />
        </Field>
        <Field label="Fixed spread (pips, blank = bps)">
          <input className={cx(input, 'num')} inputMode="decimal" value={d.spreadPips ?? ''} onChange={(e) => set('spreadPips', e.target.value.trim() === '' ? null : e.target.value)} />
        </Field>
        <Field label="Fee rate">
          <input className={cx(input, 'num')} inputMode="decimal" value={d.feeRate} onChange={(e) => set('feeRate', e.target.value)} />
        </Field>
        <Field label="Flat fee per trade ($)">
          <input className={cx(input, 'num')} inputMode="decimal" value={d.feeFlat} onChange={(e) => set('feeFlat', e.target.value)} />
        </Field>
        <Field label="Max leverage">
          <input className={cx(input, 'num text-muted')} value={cat ? `1:${cat.maxLeverage} (category)` : '—'} readOnly title="Change it on the instrument after creating it, or on its category" />
        </Field>
      </div>
      <p className="m-0 rounded-lg bg-accent-bg px-3 py-2 text-xs text-accent-fg">
        Everything marked <b>AUTO</b> was pulled from the providers and can be changed. Only pricing needs a decision. Prices start streaming within seconds of creating it.
      </p>
      {err && (
        <p className="m-0 text-xs text-down" role="alert">
          {err}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button type="button" className={secondary} onClick={onBack}>
          ← Back
        </button>
        <span className="flex-1" />
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} />
          Enable trading now
        </label>
        <button className={primary} disabled={busy}>
          {busy ? <Spinner /> : 'Create'}
        </button>
      </div>
    </form>
  );
}

export function AddInstrumentModal({ categories, onClose, onCreated }: { categories: InstrumentCategory[]; onClose: () => void; onCreated: (symbol: string) => void }) {
  const [hit, setHit] = useState<AssetSearchResult | null>(null);
  return (
    <Modal open onClose={onClose} title={hit ? 'Add instrument · review details' : 'Add instrument · find the asset'} width={hit ? 720 : 640}>
      {hit ? <DetailsStep hit={hit} categories={categories} onBack={() => setHit(null)} onCreated={onCreated} /> : <SearchStep onPick={setHit} onClose={onClose} />}
    </Modal>
  );
}

interface Usage {
  openPositions: number;
  pendingOrders: number;
  everTraded: boolean;
}

/** Archive, or (never traded) delete: refused while positions or orders are open. */
export function ArchiveModal({ inst, mode, onClose, onDone }: { inst: { id: number; symbol: string }; mode: 'archive' | 'delete'; onClose: () => void; onDone: () => void }) {
  const [u, setU] = useState<Usage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<Usage>(`/api/admin/instruments/${inst.id}/usage`)
      .then(setU)
      .catch((e) => setErr(errText(e)));
  }, [inst.id]);
  const blocked = !!u && (u.openPositions > 0 || u.pendingOrders > 0);
  const cantDelete = mode === 'delete' && !!u?.everTraded;
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'archive') await api.post(`/api/admin/instruments/${inst.id}/archive`);
      else await api.del(`/api/admin/instruments/${inst.id}`);
      onDone();
      onClose();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`${mode === 'archive' ? 'Archive' : 'Delete'} ${inst.symbol}?`} width={460}>
      <p className="m-0 text-[13px] text-muted">
        {mode === 'archive'
          ? 'Trading stops, the price feed stops and traders no longer see it. History, statements and charts are kept. You can restore it at any time.'
          : 'It is removed for good, with its chart history. Only an instrument nobody ever traded can be deleted.'}
      </p>
      {!u && !err && <Spinner />}
      {blocked && (
        <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">
          Can't {mode} yet: {u!.openPositions} open position{u!.openPositions === 1 ? '' : 's'} and {u!.pendingOrders} pending order{u!.pendingOrders === 1 ? '' : 's'} on {inst.symbol}. Close them first, or
          switch trading off with the Enabled box.
        </p>
      )}
      {cantDelete && !blocked && <p className="m-0 rounded-lg bg-down-bg px-3 py-2 text-xs text-down">{inst.symbol} has trading history, so it can only be archived.</p>}
      {err && <p className="m-0 text-xs text-down">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className={secondary} onClick={onClose}>
          Cancel
        </button>
        <button type="button" className={cx(primary, mode === 'delete' && 'bg-down')} disabled={!u || blocked || cantDelete || busy} onClick={() => void go()}>
          {busy ? <Spinner /> : mode === 'archive' ? 'Archive' : 'Delete'}
        </button>
      </div>
    </Modal>
  );
}
